'use client';

import { useState } from 'react';
import { useResponsiveSheet } from '@church/nextjs-ui/components/ResponsiveSheet';
import { Check, Plus } from 'lucide-react';
import type {
  MedicalData,
  MedicalItem,
  MedicalItemInput,
  MedicalKind,
  MedicalProfile,
  MedicalProfilePatch,
  OtcConsent,
  Person,
} from '@/lib/types';
import { KIND_LABEL, fmtDate, formatPhone, isPast } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { refetchMedical, updateMedical, useMedical } from './medicalStore';
import { EmergencyValue } from './EmergencyPage';
import { Bar, CARD, CTA_SMALL, Chip, FIELD, Kicker, LABEL, PageBody } from './ui';

const KINDS: MedicalKind[] = ['allergy', 'condition', 'medication', 'epipen', 'inhaler', 'device'];
const EXPIRES: MedicalKind[] = ['epipen', 'inhaler', 'medication'];
const KEPT: string[] = ['with them', 'nurse', 'leader', 'front office'];
const KEPT_LABEL: Record<string, string> = {
  'with them': 'With them',
  nurse: 'With the nurse',
  leader: 'With a leader',
  'front office': 'Front office',
};
const OTC: { value: OtcConsent; label: string; help: string }[] = [
  { value: 'all', label: 'Any of them', help: 'Staff may give common over-the-counter medicine.' },
  { value: 'some', label: 'Some', help: 'List the exceptions below.' },
  { value: 'callFirst', label: 'Call first', help: 'Nothing without a call home.' },
];

const EMPTY_PROFILE: MedicalProfile = {
  insuranceCarrier: null,
  insuranceGroup: null,
  insuranceMember: null,
  physicianName: null,
  physicianPhone: null,
  lastPhysicalOn: null,
  weightLbs: null,
  otcConsent: null,
  otcExceptions: null,
  lastReviewedAt: null,
};

/**
 * The medical card as filed: the items (an allergy, an EpiPen and when it
 * expires), then the card on file (insurance, physician, OTC consent), then
 * one button to say it is still right. Everything writes optimistically.
 */
export function MedicalPage({ person }: { person: Person }) {
  const { api, run, data: profileData } = useProfile();
  const { navigate } = useResponsiveSheet();
  // Shared with the Medical card on the page: a save here shows there at once.
  const data = useMedical(api, person.contactId);
  const [adding, setAdding] = useState(false);
  const [editingCard, setEditingCard] = useState(false);

  // Who to call first lives with the medical card, whatever table MP keeps it in.
  const live =
    profileData.me.contactId === person.contactId
      ? profileData.me
      : (profileData.members.find((m) => m.contactId === person.contactId) ?? person);
  const ec = live.emergencyContact;
  const linked = ec?.contactId
    ? [profileData.me, ...profileData.members].find((p) => p.contactId === ec.contactId)
    : null;
  const emergency = (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <Kicker>Emergency contact</Kicker>
        <button
          type="button"
          onClick={() => navigate('emergency')}
          className="text-xs font-semibold text-[#299c8f] hover:underline"
        >
          {ec ? 'Change' : 'Add'}
        </button>
      </div>
      <button
        type="button"
        onClick={() => navigate('emergency')}
        className={`${CARD} flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 active:bg-gray-100`}
      >
        {ec ? (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-[#1c2b39]">
              <EmergencyValue ec={ec} />
            </span>
            <span className="block truncate text-xs text-gray-500">
              {[
                ec.phone ?? linked?.mobilePhone,
                linked?.relationship?.label ??
                  (linked?.contactId === profileData.me.contactId ? 'You' : null),
              ]
                .filter(Boolean)
                .join(' · ') || 'No phone on file'}
            </span>
          </span>
        ) : (
          <span className="text-sm font-semibold text-[#299c8f]">
            Who should we call about {person.firstName}?
          </span>
        )}
      </button>
    </div>
  );

  if (data === 'error') {
    return (
      <PageBody>
        {emergency}
        <p className="text-sm text-gray-600">We couldn't load the medical card right now.</p>
        <button
          type="button"
          onClick={() => refetchMedical(api, person.contactId)}
          className={`${CTA_SMALL} self-start bg-[#1c2b39] text-white`}
        >
          Try again
        </button>
      </PageBody>
    );
  }
  if (data === null) {
    return (
      <PageBody>
        {emergency}
        <div className="flex flex-col gap-3" aria-busy="true">
          <Bar className="h-3 w-24" />
          <Bar className="h-12 w-full rounded-2xl" />
          <Bar className="h-12 w-full rounded-2xl" />
          <Bar className="h-3 w-32" />
          <Bar className="h-24 w-full rounded-2xl" />
        </div>
      </PageBody>
    );
  }
  if (data.unavailable) {
    return (
      <PageBody>
        {emergency}
        <p className="text-sm text-gray-600">
          Medical records are not available here for {person.firstName}.
        </p>
      </PageBody>
    );
  }

  const setItems = (fn: (items: MedicalItem[]) => MedicalItem[]) =>
    updateMedical(person.contactId, (d) => ({ ...d, items: fn(d.items) }));
  const setProfile = (profile: MedicalProfile | null) =>
    updateMedical(person.contactId, (d) => ({ ...d, profile }));

  const addItem = (input: MedicalItemInput) => {
    const temp: MedicalItem = {
      id: -Date.now(),
      kind: input.kind,
      typeId: input.typeId ?? null,
      label: input.label,
      severe: input.severe ?? false,
      summary: input.summary ?? null,
      detail: input.detail ?? null,
      expiresOn: input.expiresOn ?? null,
      keptWhere: input.keptWhere ?? null,
      since: new Date().toISOString().slice(0, 10),
    };
    setItems((items) => [...items, temp]);
    setAdding(false);
    void run(
      async () => {
        const saved = await api.addMedicalItem(person.contactId, input);
        setItems((items) => items.map((i) => (i.id === temp.id ? saved : i)));
      },
      () => setItems((items) => items.filter((i) => i.id !== temp.id))
    );
  };

  const removeItem = (item: MedicalItem) => {
    setItems((items) => items.filter((i) => i.id !== item.id));
    void run(
      () => api.removeMedicalItem(person.contactId, item.id),
      () => setItems((items) => [...items, item])
    );
  };

  const saveCard = (patch: MedicalProfilePatch) => {
    const before = data.profile;
    setProfile({ ...(before ?? EMPTY_PROFILE), ...patch });
    setEditingCard(false);
    void run(
      async () => setProfile(await api.updateMedicalProfile(person.contactId, patch)),
      () => setProfile(before)
    );
  };

  const confirm = () => {
    const before = data.profile;
    setProfile({
      ...(before ?? EMPTY_PROFILE),
      lastReviewedAt: new Date().toISOString().slice(0, 19),
    });
    void run(
      async () => setProfile(await api.confirmMedical(person.contactId)),
      () => setProfile(before)
    );
  };

  const profile = data.profile;

  return (
    <PageBody>
      {emergency}
      <div className="flex flex-col gap-2">
        <Kicker>On file</Kicker>
        {data.items.length === 0 && !adding && (
          <p className="text-sm text-gray-500">
            Nothing on file. That can be the answer; confirming below says it still is.
          </p>
        )}
        {data.items.length > 0 && (
          <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
            {data.items.map((m) => {
              const expired = isPast(m.expiresOn);
              return (
                <div key={m.id} className="flex items-start justify-between gap-3 px-4 py-3">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#1c2b39]">{m.label}</span>
                    <span className="block text-xs text-gray-500">
                      {KIND_LABEL[m.kind]}
                      {m.severe && <span className="font-bold text-red-600"> · Severe</span>}
                      {m.expiresOn && (
                        <span className={expired ? 'font-bold text-red-600' : ''}>
                          {' '}
                          · {expired ? 'Expired' : 'Expires'} {fmtDate(m.expiresOn)}
                        </span>
                      )}
                      {m.keptWhere && ` · ${KEPT_LABEL[m.keptWhere] ?? m.keptWhere}`}
                    </span>
                    {m.detail && (
                      <span className="mt-0.5 block text-xs text-gray-600">{m.detail}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeItem(m)}
                    className="shrink-0 text-xs font-semibold text-gray-400 transition-colors hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {adding ? (
          <AddItem types={data.types} onAdd={addItem} onCancel={() => setAdding(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className={`${CTA_SMALL} self-start bg-white text-[#1c2b39] ring-1 ring-gray-200`}
          >
            <Plus className="h-4 w-4" strokeWidth={3} />
            Add something
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <Kicker>Card on file</Kicker>
          {!editingCard && (
            <button
              type="button"
              onClick={() => setEditingCard(true)}
              className="text-xs font-semibold text-[#299c8f] hover:underline"
            >
              Edit
            </button>
          )}
        </div>
        {editingCard ? (
          <CardForm
            profile={profile ?? EMPTY_PROFILE}
            onSave={saveCard}
            onCancel={() => setEditingCard(false)}
          />
        ) : (
          <CardView profile={profile} />
        )}
      </div>

      <div className={`${CARD} flex flex-col items-start gap-2 p-4`}>
        <p className="text-sm text-gray-600">
          {profile?.lastReviewedAt
            ? `Reviewed on ${fmtDate(profile.lastReviewedAt)}.`
            : 'Not yet reviewed. A quick look and a tap keeps the card current.'}
        </p>
        <button type="button" onClick={confirm} className={`${CTA_SMALL} bg-[#1c2b39] text-white`}>
          <Check className="h-4 w-4" strokeWidth={3} />
          Still right, confirm it
        </button>
      </div>
    </PageBody>
  );
}

function CardView({ profile }: { profile: MedicalProfile | null }) {
  const rows: [string, string | null][] = [
    [
      'Insurance',
      profile?.insuranceCarrier
        ? [
            profile.insuranceCarrier,
            profile.insuranceGroup && `Group ${profile.insuranceGroup}`,
            profile.insuranceMember && `Member ${profile.insuranceMember}`,
          ]
            .filter(Boolean)
            .join(' · ')
        : null,
    ],
    [
      'Physician',
      profile?.physicianName
        ? [profile.physicianName, profile.physicianPhone].filter(Boolean).join(' · ')
        : null,
    ],
    ['Last physical', profile?.lastPhysicalOn ? fmtDate(profile.lastPhysicalOn) : null],
    ['Weight', profile?.weightLbs ? `${profile.weightLbs} lbs` : null],
    [
      'Over-the-counter',
      profile?.otcConsent
        ? `${OTC.find((o) => o.value === profile.otcConsent)?.label ?? profile.otcConsent}${profile.otcExceptions ? ` · ${profile.otcExceptions}` : ''}`
        : null,
    ],
  ];
  return (
    <div className={`${CARD} divide-y divide-gray-100 overflow-hidden`}>
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-start gap-3 px-4 py-3">
          <span className="w-32 shrink-0 text-sm text-gray-500">{label}</span>
          <span
            className={`min-w-0 flex-1 text-sm ${value ? 'font-semibold text-[#1c2b39]' : 'text-gray-400'}`}
          >
            {value ?? 'Not on file'}
          </span>
        </div>
      ))}
    </div>
  );
}

function CardForm({
  profile,
  onSave,
  onCancel,
}: {
  profile: MedicalProfile;
  onSave: (patch: MedicalProfilePatch) => void;
  onCancel: () => void;
}) {
  const [carrier, setCarrier] = useState(profile.insuranceCarrier ?? '');
  const [group, setGroup] = useState(profile.insuranceGroup ?? '');
  const [member, setMember] = useState(profile.insuranceMember ?? '');
  const [physician, setPhysician] = useState(profile.physicianName ?? '');
  const [physicianPhone, setPhysicianPhone] = useState(profile.physicianPhone ?? '');
  const [physical, setPhysical] = useState(profile.lastPhysicalOn ?? '');
  const [weight, setWeight] = useState(profile.weightLbs ? String(profile.weightLbs) : '');
  const [otc, setOtc] = useState<OtcConsent | null>(profile.otcConsent);
  const [exceptions, setExceptions] = useState(profile.otcExceptions ?? '');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      insuranceCarrier: carrier.trim() || null,
      insuranceGroup: group.trim() || null,
      insuranceMember: member.trim() || null,
      physicianName: physician.trim() || null,
      physicianPhone: physicianPhone.trim() || null,
      lastPhysicalOn: physical || null,
      weightLbs: weight ? Number(weight) : null,
      otcConsent: otc,
      otcExceptions: otc === 'some' ? exceptions.trim() || null : null,
    });
  };

  return (
    <form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-4`}>
      <label className="flex flex-col gap-1.5">
        <span className={LABEL}>Insurance carrier</span>
        <input
          value={carrier}
          onChange={(e) => setCarrier(e.target.value)}
          className={FIELD}
          maxLength={100}
        />
      </label>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Group</span>
          <input
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            className={FIELD}
            maxLength={50}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Member ID</span>
          <input
            value={member}
            onChange={(e) => setMember(e.target.value)}
            className={FIELD}
            maxLength={50}
          />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Physician</span>
          <input
            value={physician}
            onChange={(e) => setPhysician(e.target.value)}
            className={FIELD}
            maxLength={100}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Physician phone</span>
          <input
            value={physicianPhone}
            onChange={(e) => setPhysicianPhone(formatPhone(e.target.value))}
            inputMode="tel"
            className={FIELD}
          />
        </label>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Last physical</span>
          <input
            type="date"
            value={physical}
            onChange={(e) => setPhysical(e.target.value)}
            className={FIELD}
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Weight (lbs)</span>
          <input
            type="number"
            min={1}
            max={999}
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            inputMode="numeric"
            className={FIELD}
          />
        </label>
      </div>
      <div className="flex flex-col gap-2">
        <span className={LABEL}>Over-the-counter medicine</span>
        <div className="flex flex-wrap gap-2">
          {OTC.map((o) => (
            <Chip
              key={o.value}
              active={otc === o.value}
              onClick={() => setOtc(otc === o.value ? null : o.value)}
            >
              {o.label}
            </Chip>
          ))}
        </div>
        {otc && <p className="text-xs text-gray-500">{OTC.find((o) => o.value === otc)?.help}</p>}
        {otc === 'some' && (
          <input
            value={exceptions}
            onChange={(e) => setExceptions(e.target.value)}
            placeholder="No ibuprofen"
            className={FIELD}
            maxLength={200}
          />
        )}
      </div>
      <div className="flex items-center gap-3">
        <button type="submit" className={`${CTA_SMALL} bg-[#299c8f] text-white`}>
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-semibold text-gray-500 hover:underline"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Kind chips, then the standard terms for that kind (or "Something else"), then the details that kind needs. */
function AddItem({
  types,
  onAdd,
  onCancel,
}: {
  types: MedicalData['types'];
  onAdd: (input: MedicalItemInput) => void;
  onCancel: () => void;
}) {
  const [kind, setKind] = useState<MedicalKind>('allergy');
  const [typeId, setTypeId] = useState<number | null>(null);
  const [custom, setCustom] = useState(false);
  const [term, setTerm] = useState('');
  const [severe, setSevere] = useState(false);
  const [expires, setExpires] = useState('');
  const [kept, setKept] = useState<string | null>(null);
  const [detail, setDetail] = useState('');

  const options = types.filter((t) => t.kind === kind).sort((a, b) => a.sort - b.sort);
  const fixedLabel = kind === 'epipen' ? 'EpiPen' : kind === 'inhaler' ? 'Inhaler' : null;
  const label =
    fixedLabel ??
    (custom || options.length === 0
      ? term.trim()
      : (options.find((t) => t.id === typeId)?.label ?? ''));
  const canAdd = label.length > 0;

  const pickKind = (k: MedicalKind) => {
    setKind(k);
    setTypeId(null);
    setCustom(false);
    setTerm('');
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    onAdd({
      kind,
      typeId: !custom && !fixedLabel ? typeId : null,
      label,
      severe,
      expiresOn: EXPIRES.includes(kind) && expires ? expires : null,
      keptWhere: kept,
      detail: kind === 'medication' && detail.trim() ? detail.trim() : null,
    });
  };

  return (
    <form onSubmit={submit} className={`${CARD} flex flex-col gap-4 p-4`}>
      <div className="flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <Chip key={k} active={kind === k} onClick={() => pickKind(k)}>
            {KIND_LABEL[k]}
          </Chip>
        ))}
      </div>

      {!fixedLabel && (
        <div className="flex flex-col gap-2 border-t border-dashed border-gray-200 pt-3">
          {options.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {options.map((t) => (
                <Chip
                  key={t.id}
                  active={!custom && typeId === t.id}
                  onClick={() => {
                    setTypeId(t.id);
                    setCustom(false);
                  }}
                >
                  {t.label}
                </Chip>
              ))}
              <Chip
                active={custom}
                dashed
                onClick={() => {
                  setCustom(true);
                  setTypeId(null);
                }}
              >
                Something else
              </Chip>
            </div>
          )}
          {(custom || options.length === 0) && (
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={
                kind === 'medication' ? 'Cetirizine' : kind === 'allergy' ? 'Latex' : 'What is it?'
              }
              className={FIELD}
              maxLength={100}
            />
          )}
        </div>
      )}

      {kind === 'medication' && (
        <input
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="Dose and timing: 10 mg with breakfast"
          className={FIELD}
          maxLength={200}
        />
      )}

      {EXPIRES.includes(kind) && (
        <label className="flex flex-col gap-1.5">
          <span className={LABEL}>Expires</span>
          <input
            type="date"
            value={expires}
            onChange={(e) => setExpires(e.target.value)}
            className={`${FIELD} sm:max-w-xs`}
          />
        </label>
      )}

      <div className="flex flex-col gap-2">
        <span className={LABEL}>Kept where</span>
        <div className="flex flex-wrap gap-2">
          {KEPT.map((k) => (
            <Chip key={k} active={kept === k} onClick={() => setKept(kept === k ? null : k)}>
              {KEPT_LABEL[k]}
            </Chip>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          aria-pressed={severe}
          onClick={() => setSevere((v) => !v)}
          className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all hover:scale-105 active:scale-95 ${
            severe
              ? 'bg-red-600 text-white shadow-md'
              : 'bg-white text-[#1c2b39] ring-1 ring-gray-200'
          }`}
        >
          Severe
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-semibold text-gray-500 hover:underline"
        >
          Cancel
        </button>
        <button type="submit" disabled={!canAdd} className={`${CTA_SMALL} bg-[#299c8f] text-white`}>
          Add
        </button>
      </div>
    </form>
  );
}
